/* eslint eslint-comments/no-use: off */
// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {getIssueSearchQueries} from '@github-ui/item-picker-base/shared'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import type {IssuePickerItem} from '../components/IssuePicker'
import type {useIssueFilteringFragment$key} from './__generated__/useIssueFilteringFragment.graphql'
import type {useIssueFilteringQuery, useIssueFilteringQuery$data} from './__generated__/useIssueFilteringQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const useIssueFilteringQueryGraphQLQuery = graphql`
  query useIssueFilteringQuery(
    $commenters: String!
    $mentions: String!
    $assignee: String!
    $author: String!
    $other: String!
    $first: Int = 10
    $resource: URI!
    $queryIsUrl: Boolean!
    $withParent: Boolean!
  ) {
    ...useIssueFilteringFragment
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const UseIssueFilteringGraphQLFragment = graphql`
  fragment useIssueFilteringFragment on Query {
    commenters: search(type: ISSUE, first: $first, query: $commenters) {
      nodes {
        ...IssuePickerIssue @dangerously_unaliased_fixme @relay(mask: false)
        ... on Issue {
          # eslint-disable-next-line relay/unused-fields
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    mentions: search(type: ISSUE, first: $first, query: $mentions) {
      nodes {
        ...IssuePickerIssue @dangerously_unaliased_fixme @relay(mask: false)
        ... on Issue {
          # eslint-disable-next-line relay/unused-fields
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    assignee: search(type: ISSUE, first: $first, query: $assignee) {
      nodes {
        ...IssuePickerIssue @dangerously_unaliased_fixme @relay(mask: false)
        ... on Issue {
          # eslint-disable-next-line relay/unused-fields
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    author: search(type: ISSUE, first: $first, query: $author) {
      nodes {
        ...IssuePickerIssue @dangerously_unaliased_fixme @relay(mask: false)
        ... on Issue {
          # eslint-disable-next-line relay/unused-fields
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    other: search(type: ISSUE, first: $first, query: $other) {
      nodes {
        ...IssuePickerIssue @dangerously_unaliased_fixme @relay(mask: false)
        ... on Issue {
          # eslint-disable-next-line relay/unused-fields
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    # Do not return resource unless it is an Issue
    resource: resource(url: $resource) @include(if: $queryIsUrl) {
      ...IssuePickerIssue @dangerously_unaliased_fixme @relay(mask: false)
      ... on Issue {
        # eslint-disable-next-line relay/unused-fields
        parent @include(if: $withParent) {
          id
        }
      }
    }
  }
`

// Handles debouncing queries to fetch issues based on the current filter
export const useIssueFiltering = (
  filter: string,
  owner: string = '',
  repositoryNameWithOwner?: string,
  hiddenIssueIds?: string[],
  withParent: boolean = false,
): {
  isLoading: boolean
  isError: boolean
  items: Map<string, IssuePickerItem>
} => {
  const environment = useRelayEnvironment()

  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<useIssueFilteringQuery$data | null>(null)

  const activeSubscriptionRef = useRef<{unsubscribe: () => void} | null>(null)

  const fetchSearchData = useCallback(
    (nextFilter: string) => {
      setIsLoading(true)

      // Cancel any existing subscription to prevent race conditions
      activeSubscriptionRef.current?.unsubscribe()

      const subscription = clientSideRelayFetchQueryRetained<useIssueFilteringQuery>({
        environment,
        query: useIssueFilteringQueryGraphQLQuery,
        variables: {...getIssueSearchQueries(owner, nextFilter, repositoryNameWithOwner), withParent},
      }).subscribe({
        next: (innerData: useIssueFilteringQuery$data) => {
          setData(innerData ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      })

      activeSubscriptionRef.current = subscription
    },
    [environment, owner, repositoryNameWithOwner, withParent],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  useEffect(() => {
    debounceFetchSearchData(filter)
  }, [debounceFetchSearchData, filter])

  useEffect(() => {
    return () => {
      // Always clean up subscription on unmount to prevent memory leaks
      activeSubscriptionRef.current?.unsubscribe()
    }
  }, [])

  const issues = useFragment(UseIssueFilteringGraphQLFragment, data as useIssueFilteringFragment$key | null)

  const items: Map<string, IssuePickerItem> = useMemo(() => {
    if (!issues) {
      return new Map()
    } else {
      // When resource is returned, we only want to show that issue as the user's intent is to
      // select a specific issue otherwise, we show all the issues from the search queries
      if (issues.resource?.__typename === 'Issue') {
        return new Map([[issues.resource?.id, issues.resource]])
      }

      // Order the unique issues from each of the queries. Changing the order of this spread
      // will change the relative priority of each individual search query.
      const searchResultNodes = [
        ...(issues.commenters?.nodes || []),
        ...(issues.mentions?.nodes || []),
        ...(issues.assignee?.nodes || []),
        ...(issues.author?.nodes || []),
        ...(issues.other?.nodes || []),
      ]

      type searchResultNode = (typeof searchResultNodes)[0]

      const issueNodes = searchResultNodes.filter((node: searchResultNode): node is IssuePickerItem => {
        return node?.__typename === 'Issue'
      })

      const issuesMap = new Map<string, IssuePickerItem>()
      for (const issue of issueNodes) {
        if (hiddenIssueIds?.some(id => id === issue.id)) {
          continue
        }

        issuesMap.set(issue.id, issue)
      }

      return issuesMap
    }
  }, [hiddenIssueIds, issues])

  return {isLoading, isError, items}
}
